"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowLeftIcon, Star, ListMusic, Calendar, User, Play, Trash2, Mic, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/lib/i18n";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useFavorites } from "@/hooks/useFavorites";
import { usePlaylists } from "@/hooks/usePlaylists";
import { useSubscribedPodcasts } from "@/hooks/useSubscribedPodcasts";
import { motion, AnimatePresence } from "framer-motion";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { useUser } from "@/lib/context/user-context";
import { usePlayer } from "@/contexts/player-context";
import { PodcastCard } from "@/components/library/PodcastCard";
import { Card, CardContent } from "@/components/ui/card";
import { MobileMenuButton } from "@/components/ui/mobile-menu-button";

interface Episode {
  ownerAvatarUrl?: string;
  author?: string;
  title?: string;
  shareId: string;
  userId?: string;
  videoId?: string;
  publishDate?: string;
  thumbnail?: string;
  duration?: number;
}

function EpisodeCard({ episode, onRemove, showRemoveButton = false }: { episode: Episode; onRemove?: () => void; showRemoveButton?: boolean }) {
  const { lang } = useI18n() as any;
  const { setCurrentEpisode } = usePlayer();
  const episodeUrl = `/episodes/${episode.shareId}`;

  const formatDuration = (seconds?: number) => {
    if (!seconds) return "00:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handlePlayClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Set episode in global player context
    const episodeData = {
      shareId: episode.shareId,
      content: {
        title: episode.title,
        author: episode.author,
        ownerAvatarUrl: episode.ownerAvatarUrl,
        thumbnail: episode.thumbnail,
        videoId: episode.videoId,
        duration: episode.duration
      }
    };
    
    setCurrentEpisode(episodeData);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.3 }}
      className="group relative"
    >
      <Link 
        href={episodeUrl}
        className="block w-full cursor-pointer transition-all duration-200 hover:bg-primary/5 p-1 rounded-xl border border-border/50 hover:border-primary/20"
      >
        {/* Card Content */}
        <div className="flex gap-4">
          {/* Thumbnail */}
          <div className="flex-shrink-0 w-[120px] h-[70px] bg-muted rounded-lg overflow-hidden relative">
            {episode.thumbnail ? (
              <Image 
                src={episode.thumbnail} 
                alt={episode.title || "Episode"}
                fill
                className="object-cover group-hover:scale-110 transition-transform duration-300"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                <Play className="w-6 h-6 text-primary/40" />
              </div>
            )}
            
            {/* Play button overlay */}
            <button
              onClick={handlePlayClick}
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-primary/90 hover:bg-primary text-primary-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg z-10"
            >
              <Play className="w-4 h-4 ml-0.5" />
            </button>
            
            {/* Duration overlay */}
            {episode.duration && (
              <div className="absolute bottom-1 right-1 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded">
                {formatDuration(episode.duration)}
              </div>
            )}
          </div>
          
          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Title */}
            <h3 className="text-base font-medium mb-2 line-clamp-2 group-hover:text-primary transition-colors">
              {episode.title || "Untitled Episode"}
            </h3>
            
            {/* Metadata */}
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              {/* Author */}
              {episode.author && (
                <div className="flex items-center gap-1.5">
                  <div className="w-5 h-5 rounded-full overflow-hidden bg-primary/15">
                    {episode.ownerAvatarUrl ? (
                      <Image
                        src={episode.ownerAvatarUrl}
                        alt={episode.author}
                        width={20}
                        height={20}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <User className="w-3 h-3" />
                      </div>
                    )}
                  </div>
                  <span className="truncate max-w-[150px]">{episode.author}</span>
                </div>
              )}
              
              {/* Date */}
              {episode.publishDate && (
                <div className="flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5" />
                  <span>
                    {new Date(episode.publishDate).toLocaleDateString(lang === 'chinese' ? 'zh-CN' : 'en-US', {
                      month: "short",
                      day: "numeric",
                      year: "numeric"
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </Link>
      
      {/* Remove button */}
      {showRemoveButton && onRemove && (
        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.preventDefault();
            onRemove();
          }}
          className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      )}
    </motion.div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="p-3 rounded-xl border border-border/50 animate-pulse">
          <div className="flex gap-4">
            <div className="w-[120px] h-[90px] bg-muted rounded-lg" />
            <div className="flex-1 space-y-3">
              <div className="h-5 bg-muted rounded w-3/4" />
              <div className="h-4 bg-muted rounded w-1/2" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function LibraryPage() {
  const { t, lang } = useI18n() as any;
  const { user } = useUser();
  const { favorites, removeFromFavorites } = useFavorites();
  const { playlists, episodes: playlistEpisodes, isLoading: playlistsLoading, removeEpisodeFromPlaylist } = usePlaylists(true); // Enable auto-loading for library page
  const { subscribedPodcasts, isLoading: podcastsLoading, refetchSubscriptions } = useSubscribedPodcasts();
  const [activeTab, setActiveTab] = useState("favorites");

  // Fetch favorites data when available
  const [favoritesData, setFavoritesData] = useState<Episode[]>([]);
  const [fetchingFavorites, setFetchingFavorites] = useState(false);

  const userId = user?.id ?? null;

  const mobileMenuButton = (
    <MobileMenuButton ariaLabel={lang === 'chinese' ? '打开菜单' : 'Open menu'} />
  );

  useEffect(() => {
    if (!userId) {
      setFavoritesData((prev) => (prev.length === 0 ? prev : []));
      setFetchingFavorites(false);
      return;
    }

    const fetchFavoritesData = async () => {
      if (!favorites || favorites.length === 0) {
        setFavoritesData((prev) => (prev.length === 0 ? prev : []));
        return;
      }

      setFetchingFavorites(true);
      try {
        const episodesData = await Promise.all(
          favorites.map(async (shareId) => {
            try {
              const response = await fetch(`/api/episodes/${shareId}`);
              if (response.ok) {
                const data = await response.json();
                return {
                  shareId,
                  title: data.content?.title,
                  author: data.content?.author,
                  ownerAvatarUrl: data.content?.ownerAvatarUrl,
                  thumbnail: data.content?.thumbnail,
                  publishDate: data.content?.publishDate,
                  duration: data.content?.duration,
                  videoId: data.content?.videoId,
                };
              }
              return { shareId };
            } catch {
              return { shareId };
            }
          })
        );
        setFavoritesData(episodesData);
      } catch (error) {
        console.error("Error fetching favorites data:", error);
      } finally {
        setFetchingFavorites(false);
      }
    };

    fetchFavoritesData();
  }, [favorites, userId]);

  if (!user) {
    return (
      <>
        {mobileMenuButton}
        <div className="min-h-screen flex items-center justify-center">
          <Card className="w-full max-w-md">
            <CardContent className="text-center py-8">
              <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <h2 className="text-lg font-semibold mb-2">{t('savedWords.loginRequiredTitle')}</h2>
              <p className="text-sm text-muted-foreground mb-4">{t('savedWords.loginRequiredDesc')}</p>
              <Button asChild>
                <Link href="/login">{t('common.signIn')}</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </>
    );
  }

  const handleRemoveFavorite = async (shareId: string) => {
    await removeFromFavorites(shareId);
  };

  const handleRemoveFromPlaylist = async (playlistId: string, episodeId: string) => {
    await removeEpisodeFromPlaylist(playlistId, episodeId);
  };

  const isLoading = playlistsLoading || fetchingFavorites || podcastsLoading;

  return (
    <>
      {mobileMenuButton}
      <AuthGuard>
        {/* Header */}
        <div className="my-4 space-y-4">
          <div className="flex items-center">
            <Button variant="ghost" className="h-8" asChild>
              <Link href="/">
                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                <span className={lang === 'chinese' ? 'font-normal' : ''}>{t('common.backToHome')}</span>
              </Link>
            </Button>
          </div>
          
          <div className="space-y-2">
            <h1 className="text-2xl sm:text-3xl font-bold">{t('library.title')}</h1>
            {/* <p className="text-muted-foreground">
              Your saved episodes and playlists
            </p> */}
          </div>
        </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full max-w-[600px] grid-cols-3 mb-8">
              <TabsTrigger value="favorites" className="flex items-center gap-2">
                <Star className="w-4 h-4" />
                <span className={lang === 'chinese' ? 'font-normal' : ''}>{t('library.tabs.favorites')}</span>
                {!isLoading && favorites && (
                  <span className="ml-1 text-xs bg-primary/20 px-2 py-0.5 rounded-full">
                    {favorites.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="playlists" className="flex items-center gap-2">
                <ListMusic className="w-4 h-4" />
                <span className={lang === 'chinese' ? 'font-normal' : ''}>{t('library.tabs.playlists')}</span>
                {!isLoading && playlists && (
                  <span className="ml-1 text-xs bg-primary/20 px-2 py-0.5 rounded-full">
                    {playlists.length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="podcasts" className="flex items-center gap-2">
                <Mic className="w-4 h-4" />
                <span className={lang === 'chinese' ? 'font-normal' : ''}>{t('library.tabs.podcasts')}</span>
                {!podcastsLoading && subscribedPodcasts && (
                  <span className="ml-1 text-xs bg-primary/20 px-2 py-0.5 rounded-full">
                    {subscribedPodcasts.length}
                  </span>
                )}
              </TabsTrigger>
            </TabsList>

            {/* Favorites Tab */}
            <TabsContent value="favorites" className="space-y-4">
              {isLoading ? (
                <LoadingSkeleton />
              ) : favoritesData.length > 0 ? (
                <div className="grid gap-2">
                  <AnimatePresence mode="popLayout">
                    {favoritesData.map((episode) => (
                      <EpisodeCard 
                        key={episode.shareId} 
                        episode={episode}
                        onRemove={() => handleRemoveFavorite(episode.shareId)}
                        showRemoveButton
                      />
                    ))}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Star className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
                  <h3 className="text-lg font-medium mb-2">{t('library.emptyFavoritesTitle')}</h3>
                  <p className="text-muted-foreground mb-6">{t('library.emptyFavoritesDesc')}</p>
                  <Button asChild>
                    <Link href="/episodes">{t('library.browseEpisodes')}</Link>
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Playlists Tab */}
            <TabsContent value="playlists" className="space-y-6">
              {isLoading ? (
                <LoadingSkeleton />
              ) : playlists && playlists.length > 0 ? (
                <div className="space-y-8">
                  {playlists.map((playlist) => (
                    <div key={playlist.id} className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h3 className="text-xl font-semibold flex items-center gap-2">
                          <ListMusic className="w-5 h-5" />
                          {playlist.name}
                        </h3>
                        <span className="text-sm text-muted-foreground">
                          {playlistEpisodes?.[playlist.id]?.length || 0} {lang === 'chinese' ? '个节目' : 'episodes'}
                        </span>
                      </div>
                      
                      {playlistEpisodes?.[playlist.id] && playlistEpisodes[playlist.id].length > 0 ? (
                        <div className="grid gap-4 ml-7">
                          <AnimatePresence mode="popLayout">
                            {playlistEpisodes?.[playlist.id]?.map((episode) => (
                              <EpisodeCard 
                                key={`${playlist.id}-${episode.shareId}`} 
                                episode={episode}
                                onRemove={() => handleRemoveFromPlaylist(playlist.id, episode.shareId)}
                                showRemoveButton
                              />
                            ))}
                          </AnimatePresence>
                        </div>
                      ) : (
                        <div className="ml-7 p-8 border border-dashed border-border/50 rounded-lg text-center">
                          <p className="text-muted-foreground">{t('library.playlistEmpty')}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <ListMusic className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
                  <h3 className="text-lg font-medium mb-2">{t('library.playlistsEmptyTitle')}</h3>
                  <p className="text-muted-foreground mb-6">{t('library.playlistsEmptyDesc')}</p>
                  <Button asChild>
                    <Link href="/episodes">{t('library.browseEpisodes')}</Link>
                  </Button>
                </div>
              )}
            </TabsContent>

            {/* Podcasts Tab */}
            <TabsContent value="podcasts" className="space-y-4">
              {podcastsLoading ? (
                <LoadingSkeleton />
              ) : subscribedPodcasts && subscribedPodcasts.length > 0 ? (
                <div className="grid gap-2">
                  <AnimatePresence mode="popLayout">
                    {subscribedPodcasts.map((podcast) => (
                      <PodcastCard
                        key={podcast.id}
                        podcast={podcast}
                        userId={user?.id}
                        onUnfollow={refetchSubscriptions}
                      />
                    ))}
                  </AnimatePresence>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Mic className="w-12 h-12 mx-auto mb-4 text-muted-foreground/40" />
                  <h3 className="text-lg font-medium mb-2">{t('library.podcastsEmptyTitle')}</h3>
                  <p className="text-muted-foreground mb-6">{t('library.podcastsEmptyDesc')}</p>
                  <Button asChild>
                    <Link href="/search">{t('library.discoverPodcasts')}</Link>
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>
      </AuthGuard>
    </>
  );
}
